import { IUser } from '@/entities/user/model/types'
import { Button, Dialog, DialogContent, DialogFooter, DialogTitle, DialogTrigger, Label } from '@/shared/ui'
import { Edit2Icon } from 'lucide-react'
import { useEffect, useState } from 'react'
import useRoles, { convertRoleName } from '../../lib/use-roles'
import { CheckboxGroup } from '@/shared/ui/checbox-group'
import useNotifier from '@/shared/lib/use-notifier'
import { ContractsStore } from '@/shared/model/store/contracts-store'
import { stringToBytes32 } from '@/shared/lib/web3utils'
import { LangStore } from '@/shared/model/store'
import SearchUserComponent from '@/features/search-user/ui'

type Props = {
	user?: IUser
	npo_address: `0x${string}`
	reload: () => void
	isAdmin?: boolean
}

const MemberModal = ({ user, npo_address, reload, isAdmin }: Props) => {
	const [isOpen, setIsOpen] = useState(false)
	const { langPack } = LangStore()
	const { getRoles, roles } = useRoles()
	const { writeContract, isLoading } = useNotifier({
		onSuccess() {
			reload()
			setIsOpen(false)
		}
	})
	const { contracts } = ContractsStore()
	const [selectedUser, setSelectedUser] = useState<IUser | null>(null)
	const [selectedRoles, setSelectedRoles] = useState<string[]>([])

	const roleOptions = roles.map(role => ({
		id: role,
		label: convertRoleName(role)
	}))

	const openHandler = state => {
		if (!state) {
			setSelectedUser(null)
			setSelectedRoles([])
		}
		setIsOpen(state)
	}

	const onOk = () => {
		if (!selectedUser?.address) return
		writeContract({
			abi: contracts.SavvaNPO.abi,
			address: npo_address,
			functionName: !user ? 'addMember' : 'changeMemberRoles',
			args: [selectedUser.address, selectedRoles]
		})
	}

	const setAdmin = () => {
		if (!user?.address) return
		writeContract({
			abi: contracts.SavvaNPO.abi,
			address: npo_address,
			functionName: 'addAdmin',
			args: [user.address]
		})
	}

	const removeAdmin = () => {
		if (!user?.address) return
		writeContract({
			abi: contracts.SavvaNPO.abi,
			address: npo_address,
			functionName: 'removeAdmin',
			args: [user.address]
		})
	}

	useEffect(() => {
		if (!isOpen) return
		getRoles(npo_address)
		if (user) {
			setSelectedUser(user)
			const rb32 = user.roles.map(role => stringToBytes32(role))
			setSelectedRoles(rb32)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen])

	return (
		<Dialog onOpenChange={openHandler} open={isOpen}>
			<DialogTrigger>
				{user ? (
					<Button size={'icon'} variant={'secondary'}>
						<Edit2Icon size={12} />
					</Button>
				) : (
					<Button variant={'orange'} size={'sm'}>
						{langPack('action.add')}
					</Button>
				)}
			</DialogTrigger>
			<DialogContent>
				<DialogTitle>{user ? langPack('npo.edit_user') : langPack('npo.add_user')}</DialogTitle>
				<div className='w-full rounded-lg p-4 shadow-md'>
					<SearchUserComponent onSelect={setSelectedUser} selected={selectedUser} />
				</div>
				<div className='w-full rounded-lg p-4 shadow-md'>
					<div className='mb-2 flex w-full items-center justify-between border-b-[1px] border-[#d1d5db] pb-2'>
						<Label>Роли</Label>
					</div>
					<div>
						<CheckboxGroup options={roleOptions} setSelected={setSelectedRoles} selected={selectedRoles} />
					</div>
				</div>
				{user?.address && (
					<Button className='w-full' variant={'orange'} size='sm' onClick={isAdmin ? removeAdmin : setAdmin}>
						{isAdmin ? langPack('npo.remove_admin') : langPack('npo.add_admin')}
					</Button>
				)}

				<DialogFooter>
					<Button
						variant={'orange'}
						onClick={onOk}
						loading={isLoading}
						disabled={!selectedUser?.address || !selectedRoles.length}
					>
						{langPack('action.save')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default MemberModal
